import { DIFFICULTY_CONFIG } from '../../../config/difficulty.config';
import { WORLD_CONFIG } from '../../../config/world.config';
import { DifficultyManager } from '../../../difficulty/DifficultyManager';
import { ObstacleType } from '../../../obstacles/ObstacleType';
import { deckLandingZ, deckLineHeight, type DeckProfile } from '../deckLines';
import {
  COLLECTIBLE_FLOAT_HEIGHT,
  collectibleLine,
  entryConnector,
  laneArea,
  laneCenterX,
  moduleSides,
  obstacleRowOn,
  standardLanes,
  straightExit,
} from '../segmentBuilders';
import {
  SegmentKind,
  type CeilingZone,
  type CollectibleSpawnPoint,
  type FixedObstaclePlacement,
  type SegmentDefinition,
  type SegmentGenerationRules,
} from '../SegmentDefinition';

/**
 * SEG_07 – pipe passage 40 m (design/01_segments/SEG_07_Pipe_Passage; the sheet's 30 m
 * standardised to 40 m). Large concrete pipes (DN 2000) lie lengthwise on the outer lanes –
 * run (or slide) through them (2.0 m inside: no jumping, no lane change) or, with a double jump,
 * up onto them and along their crown (user 2026-10-02: "oben drüber … auf den Rohren laufen");
 * low pipe bridges cross two lanes (slide – or double-jump over them); small cable drums block
 * the middle lane (jump).
 *
 *   Bridge B  z 6 over lanes 1–2          Pipe A  lane 0, z 12–20    Pipe B  lane 2, z 20–28
 *   Bridge A  z 35 over lanes 0–1         (1.3 m clearance, post between the lanes)
 *   Drums     z 16 / z 26 in the middle lane (walls on both sides there: jump)
 *
 *   Crown     2.15 m (2 × inner radius + wall): a double jump gets up (rise 2.15 m, step limit 2.7 m).
 *             JumpReach (1/120 s steps): 0.45 s input window for the second jump at best, ≥ 0.2 s when
 *             the face is met 0.34–1.15 s after take-off. Run-up lane 0: the module's first 12 m (0.31 s
 *             of take-off timing at 18 m/s), lane 2: 13.25 m after bridge B (0.40 s) – terrainAnalysis
 *             proves it (decks), the validator keeps both lanes free before the faces,
 *             tests/unit/walkableTops.test.ts replays it with the PlayerController.
 *   Front     the pipe mouth is open below 2.0 m (run / slide in); feet between the road and a step
 *             below the crown meet the ring – a frontal hit, as before.
 *   Drop      off the end: 2.15 m fall, landing ≤ 8.1 m later at 18 m/s – the lane stays free for the
 *             fall plus one reaction time (13.5 m): pipe A's lane until bridge A, pipe B's to the end.
 *   Bottles   lines through every lane (inside the pipes, under the bridges, over the drums) or up onto a
 *             pipe crown (the double-jump flight, along the crown, the fall) – the top route pays.
 *
 * Bridges (user 2026-10-02: "Hindernisse … mit einem Sprung oder Doppelsprung überspringen"): the
 * steel pipeline ends where it visibly ends – DN 900 main pipe 1.3–2.2 m, the small pipe on its front
 * half up to 2.62 m (ENV_PipePassage_A). Their row stays a slide; a double jump (feet up to 2.93 m)
 * clears them or lands on them (a step below the top) and drops off – the post under the free end
 * ends there too.
 * Jump-over lines at 8 and 18 m/s, landing clear of the drums: tests/unit/jumpHonesty.test.ts.
 */
export const PIPES = {
  length: 40,
  /** Inner radius of the walk-in pipe; its floor is the road, so the roof is 2 × r high. */
  innerRadius: 1,
  wall: 0.15,
  pipeA: { lane: 0, zFrom: 12, zTo: 20 },
  pipeB: { lane: 2, zFrom: 20, zTo: 28 },
  /**
   * Main pipe (depth = its diameter) from `clearance` up; the small pipe on top of it `topPipeGap` above its
   * crown, its axis `topPipeOffset` along the track from the bridge's (Blender: BRIDGE "small" 0.36 m, 0.06 m
   * saddle gap, z − 0.12) – see BRIDGE_PIPE_TOP / BRIDGE_TOP.
   */
  bridge: { clearance: 1.3, depth: 0.9, post: 0.2, postDepth: 0.5, topPipe: 0.36, topPipeGap: 0.06, topPipeOffset: -0.12 },
  bridgeA: { z: 35, fromLane: 0, toLane: 1 },
  bridgeB: { z: 6, fromLane: 1, toLane: 2 },
  drumRows: { D1: 16, D2: 26 },
  collectibleSpacing: 2,
} as const;

/** Crown of a walk-in pipe (its walkable top): the bore stands on the road, so 2 × inner radius + wall. */
export const PIPE_CROWN = 2 * PIPES.innerRadius + PIPES.wall;

/** Crown of a bridge's main pipe (2.2 m) – the walkable top of the bridge beside the small pipe. */
export const BRIDGE_PIPE_TOP = PIPES.bridge.clearance + PIPES.bridge.depth;
/** Visible top of a pipe bridge (2.62 m): main pipe crown + saddle gap + the small pipe on top. */
export const BRIDGE_TOP = BRIDGE_PIPE_TOP + PIPES.bridge.topPipeGap + PIPES.bridge.topPipe;

const DRUM_TYPES = [ObstacleType.Jump] as const;
/** The middle lane – the outer lanes keep their run-ups and drop zones free (the crown route). */
const MIDDLE_LANE = 1;

type PipeSpec = { readonly lane: number; readonly zFrom: number; readonly zTo: number };

/**
 * Walk-in pipe on a lane: a 2 m roof over the lane, the shell towards the center lane is a wall.
 * Both are slabs up to the crown: a runner who gets up there runs along the top.
 */
function pipe(name: string, spec: PipeSpec): CeilingZone[] {
  const x = laneCenterX(WORLD_CONFIG, spec.lane);
  const inward = -Math.sign(x); // towards the center lane
  const innerFace = x + inward * PIPES.innerRadius;
  const outerFace = innerFace + inward * PIPES.wall;
  return [
    {
      id: `CEIL_${name}_ROOF`,
      zFrom: spec.zFrom,
      zTo: spec.zTo,
      xFrom: x - PIPES.innerRadius,
      xTo: x + PIPES.innerRadius,
      clearance: 2 * PIPES.innerRadius,
      top: PIPE_CROWN,
    },
    {
      id: `CEIL_${name}_SHELL`,
      zFrom: spec.zFrom,
      zTo: spec.zTo,
      xFrom: Math.min(innerFace, outerFace),
      xTo: Math.max(innerFace, outerFace),
      clearance: WORLD_CONFIG.groundHeight, // full-height wall – up to the crown
      top: PIPE_CROWN,
    },
  ];
}

/**
 * Pipe bridge over a pair of lanes; its free end rests on a post between the lanes. Slabs up to the visible
 * tops: the main pipe (BRIDGE_PIPE_TOP) and the small pipe on its front half (BRIDGE_TOP) – slid under, or
 * double-jumped over / onto, never an invisible wall above the pipes.
 */
function bridge(name: string, spec: { readonly z: number; readonly fromLane: number; readonly toLane: number }): CeilingZone[] {
  const B = PIPES.bridge;
  const area = laneArea(WORLD_CONFIG, spec.fromLane, spec.toLane, spec.z - B.depth / 2, spec.z + B.depth / 2);
  const freeEndRight = spec.toLane < WORLD_CONFIG.laneCount - 1;
  const postX = freeEndRight ? area.xTo : area.xFrom;
  const topPipeZ = spec.z + B.topPipeOffset;
  return [
    { id: `CEIL_${name}`, ...area, clearance: B.clearance, top: BRIDGE_PIPE_TOP },
    // the small pipe rests on saddles on the main pipe – solid from its crown up
    { id: `CEIL_${name}_TOP`, ...area, zFrom: topPipeZ - B.topPipe / 2, zTo: topPipeZ + B.topPipe / 2, clearance: BRIDGE_PIPE_TOP, top: BRIDGE_TOP },
    {
      id: `CEIL_${name}_POST`,
      zFrom: spec.z - PIPES.bridge.postDepth / 2,
      zTo: spec.z + PIPES.bridge.postDepth / 2,
      xFrom: freeEndRight ? postX : postX - PIPES.bridge.post,
      xTo: freeEndRight ? postX + PIPES.bridge.post : postX,
      clearance: WORLD_CONFIG.groundHeight, // the post carries the pipes: a wall up to their top
      top: BRIDGE_TOP,
    },
  ];
}

const GENERATION: SegmentGenerationRules = {
  weight: 0.25,
  minDistance: 300,
  minSegmentsAfterJunction: 1,
  family: 'PIPES',
  tags: ['pipes', 'terrain', 'slide', 'platform', 'double-jump'],
};

// ── Obstacles ────────────────────────────────────────────────────────────────

const at = (row: keyof typeof PIPES.drumRows, obstacleId: string): FixedObstaclePlacement => ({
  spawnPointId: `SOCKET_OBS_${row}_L${MIDDLE_LANE}`,
  obstacleId,
});

// ── Bottles ──────────────────────────────────────────────────────────────────

/** Bottle lines show the ideal path at the speed the module is first met. */
const LINE_SPEED = new DifficultyManager(DIFFICULTY_CONFIG).speedAt(GENERATION.minDistance);
const SPACING = PIPES.collectibleSpacing;
/** First and last bottle of a line (one spacing inside the module ends, like collectibleLanes before). */
const LINE_FROM = 1;
const LINE_TO = PIPES.length - 1;
const flat = () => COLLECTIBLE_FLOAT_HEIGHT;

/** Ground line through one lane: inside the pipe, under the bridges, over the drums (populator arcs). */
const groundLine = (lane: number): CollectibleSpawnPoint[] =>
  collectibleLine(WORLD_CONFIG, `LANE_${lane}`, [{ lane, from: LINE_FROM, to: LINE_TO, spacing: SPACING, height: flat }]);

/** Up onto a pipe's crown with the double jump, along it, the drop – own socket ids (the lane is shared with its ground line). */
function crownLine(name: string, spec: PipeSpec): CollectibleSpawnPoint[] {
  const deck: DeckProfile = { face: spec.zFrom, end: spec.zTo, top: PIPE_CROWN };
  // The line ends one bottle after the drop has reached the road.
  const to = Math.min(LINE_TO, Math.ceil(deckLandingZ(deck, LINE_SPEED)) + SPACING);
  return collectibleLine(WORLD_CONFIG, name, [{ lane: spec.lane, from: LINE_FROM, to, spacing: SPACING, height: deckLineHeight(deck, LINE_SPEED) }]).map((point) => ({
    ...point,
    id: point.id.replace('SOCKET_COL_', `SOCKET_COL_${name}_`),
  }));
}

const collectibles: CollectibleSpawnPoint[] = [
  ...groundLine(MIDDLE_LANE), // first line = the one safe placements take
  ...groundLine(PIPES.pipeA.lane),
  ...groundLine(PIPES.pipeB.lane),
  ...crownLine('CROWN_A', PIPES.pipeA),
  ...crownLine('CROWN_B', PIPES.pipeB),
];

export const SEG_PIPES_40M: SegmentDefinition = {
  id: 'SEG_PIPES_40M',
  kind: SegmentKind.Straight,
  displayName: 'Rohrpassage 40 m',
  length: PIPES.length,
  width: WORLD_CONFIG.trackWidth,
  // Pipe passage overlay (ENV_PipePassage_A) on two base road modules.
  visualAssetId: 'SEG_PIPES_40M',
  extraVisuals: [
    { assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z: 0 } },
    { assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z: 20 } },
  ],

  entry: entryConnector(),
  exits: [straightExit(PIPES.length)],
  lanes: standardLanes(WORLD_CONFIG),

  terrain: {
    surfaces: [],
    gaps: [],
    ceilings: [...bridge('BRIDGE_B', PIPES.bridgeB), ...pipe('PIPE_A', PIPES.pipeA), ...pipe('PIPE_B', PIPES.pipeB), ...bridge('BRIDGE_A', PIPES.bridgeA)],
  },

  obstacleSpawnPoints: [
    ...obstacleRowOn(WORLD_CONFIG, 'D1', PIPES.drumRows.D1, [MIDDLE_LANE], DRUM_TYPES),
    ...obstacleRowOn(WORLD_CONFIG, 'D2', PIPES.drumRows.D2, [MIDDLE_LANE], DRUM_TYPES),
  ],
  obstacleRowLayouts: { sparse: [], dense: [] },
  obstacleVariants: [
    { id: 'DRUMS_BOTH', weight: 1, placements: [at('D1', 'OBS_CABLE_DRUM_A'), at('D2', 'OBS_CABLE_DRUM_A')] },
    { id: 'DRUM_FIRST', weight: 1, placements: [at('D1', 'OBS_CABLE_DRUM_A')] },
    { id: 'DRUM_SECOND', weight: 1, placements: [at('D2', 'OBS_CABLE_DRUM_A')] },
  ],

  collectibleSpawnPoints: collectibles,

  ...moduleSides(PIPES.length),

  generation: GENERATION,
};
